// =============================================================================
// psUI — Design tokens + componentes compartilhados do módulo Processo Seletivo
// =============================================================================
// Padroniza o visual das telas internas do recrutamento (antes cada uma tinha sua cor).
// Regra de cor (decidida 2026-06-30):
//   - COR DE ACENTO única = Rosa/Vinho #e11d48 -> unifica o "cromo" (banner, header de
//     tabela, abas ativas, botões primários, realces).
//   - SEMÂNTICA DE STATUS é separada: um badge "Aprovado" é verde, "Reprovado" é vermelho,
//     etc. — senão a cor de acento (vermelha) faria "aprovado" parecer alerta.
// Estilo do projeto: inline styles em JS (NÃO Tailwind), fonte Raleway, cards arredondados.

import React from 'react';

// ─── Cor de acento do módulo + neutros ───────────────────────────────────────
export const CORES = {
    primaria: '#e11d48',
    primariaEscura: '#be185d',
    primariaBg: '#ffe4e6',      // fundo claro do acento (badges/realces)
    gradiente: 'linear-gradient(135deg, #e11d48 0%, #be185d 100%)',

    // Neutros (mesmos tons dos outros módulos padronizados)
    texto: '#0f172a',
    textoSec: '#475569',
    textoFraco: '#64748b',
    borda: '#e2e8f0',
    bgPagina: '#f8fafc',
    bgSuave: '#f8fafc',
    branco: '#fff',
};

// ─── Semântica de status (badges) — NÃO usar a cor de acento aqui ─────────────
// Cada chave: { bg, fg } no padrão pílula. Reaproveitável por todas as telas.
export const STATUS = {
    aprovado:   { bg: '#ecfdf5', fg: '#047857' }, // verde
    emProcesso: { bg: '#eff6ff', fg: '#1d4ed8' }, // azul
    inscrito:   { bg: '#f5f3ff', fg: '#6d28d9' }, // roxo
    reprovado:  { bg: '#fff1f2', fg: '#be123c' }, // vermelho
    desistente: { bg: '#fffbeb', fg: '#b45309' }, // âmbar
    neutro:     { bg: '#f1f5f9', fg: '#475569' }, // cinza
};

// ─── Badge de status (pílula) ────────────────────────────────────────────────
// <BadgeStatus tom="aprovado">Contratado</BadgeStatus>
// ou cor explícita: <BadgeStatus cor={{bg,fg}}>...</BadgeStatus>
// `title` é opcional — vira tooltip nativo do browser (ex.: quem registrou a desistência).
export function BadgeStatus({ tom = 'neutro', cor, children, icone: Icone, title }) {
    const c = cor || STATUS[tom] || STATUS.neutro;
    return (
        <span title={title} style={{
            display: 'inline-flex', alignItems: 'center', gap: '4px',
            padding: '4px 10px', borderRadius: '999px', fontSize: '11px',
            fontWeight: 800, whiteSpace: 'nowrap', backgroundColor: c.bg, color: c.fg,
        }}>
            {Icone && <Icone size={12} />}
            {children}
        </span>
    );
}

// ─── Botão padronizado ───────────────────────────────────────────────────────
// variant: 'primario' (acento), 'secundario' (outline), 'perigo', 'sucesso', 'neutro'
const VARIANTES_BOTAO = {
    primario:   { backgroundColor: CORES.primaria, color: '#fff', border: 'none' },
    secundario: { backgroundColor: '#fff', color: CORES.textoSec, border: `1px solid ${CORES.borda}` },
    perigo:     { backgroundColor: '#fff', color: '#b91c1c', border: '1px solid #fecaca' },
    sucesso:    { backgroundColor: '#059669', color: '#fff', border: 'none' },
    neutro:     { backgroundColor: '#0f172a', color: '#fff', border: 'none' },
};
export function Botao({ variant = 'primario', icone: Icone, children, style, disabled, ...rest }) {
    const v = VARIANTES_BOTAO[variant] || VARIANTES_BOTAO.primario;
    return (
        <button
            disabled={disabled}
            style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                padding: '10px 18px', borderRadius: '8px', fontSize: '14px', fontWeight: 700,
                cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1,
                transition: 'all 0.2s', ...v, ...style,
            }}
            {...rest}
        >
            {Icone && <Icone size={16} />}
            {children}
        </button>
    );
}

// ─── Cartão de KPI (número grande) ───────────────────────────────────────────
// <CartaoKpi titulo="Total" valor={42} cor="#3b82f6" icone={Users} />
export function CartaoKpi({ titulo, valor, cor = CORES.primaria, icone: Icone, sufixo, badge }) {
    return (
        <div style={{
            backgroundColor: '#fff', padding: '20px', borderRadius: '14px',
            border: `1px solid ${CORES.borda}`, borderLeft: `4px solid ${cor}`,
            boxShadow: '0 2px 4px rgba(0,0,0,0.03)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
        }}>
            <div>
                <h4 style={{ color: CORES.textoFraco, fontSize: '12px', textTransform: 'uppercase', margin: '0 0 8px 0', fontWeight: 800, letterSpacing: '0.3px' }}>{titulo}</h4>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '28px', fontWeight: 900, color: CORES.texto, lineHeight: 1 }}>{valor}{sufixo}</span>
                    {badge && <span style={{ fontSize: '11px', fontWeight: 800, color: cor, backgroundColor: hexParaBg(cor), padding: '3px 8px', borderRadius: '999px', whiteSpace: 'nowrap' }}>{badge}</span>}
                </div>
            </div>
            {Icone && (
                <div style={{ padding: '12px', borderRadius: '12px', backgroundColor: hexParaBg(cor), color: cor, flexShrink: 0 }}>
                    <Icone size={24} />
                </div>
            )}
        </div>
    );
}

// ─── Estilos prontos (objetos reusáveis) ─────────────────────────────────────
export const estilos = {
    // Título de seção da tela (ex.: "Monitoramento de Candidatos")
    tituloSecao: { fontSize: '22px', fontWeight: 800, color: CORES.texto, margin: 0 },
    subtituloSecao: { fontSize: '14px', color: CORES.textoFraco, margin: '4px 0 0 0' },

    // Inputs/selects de filtro
    input: {
        width: '100%', padding: '10px', borderRadius: '8px', border: `1px solid #cbd5e1`,
        backgroundColor: '#fff', color: CORES.texto, fontSize: '14px', boxSizing: 'border-box',
    },
    rotuloFiltro: { display: 'block', fontSize: '12px', fontWeight: 700, color: CORES.textoSec, marginBottom: '6px' },

    // Barra de filtros (container)
    barraFiltros: {
        backgroundColor: '#fff', padding: '16px', borderRadius: '12px',
        border: `1px solid ${CORES.borda}`, marginBottom: '20px',
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '14px', alignItems: 'end',
    },

    // Tabela
    tabelaWrap: { backgroundColor: '#fff', borderRadius: '12px', border: `1px solid ${CORES.borda}`, overflow: 'hidden' },
    tabela: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
    th: {
        padding: '12px 16px', textAlign: 'left', fontWeight: 800, fontSize: '11px',
        textTransform: 'uppercase', letterSpacing: '0.3px', color: CORES.textoSec,
        backgroundColor: CORES.bgSuave, borderBottom: `2px solid ${CORES.primaria}`, whiteSpace: 'nowrap',
    },
    td: { padding: '12px 16px', color: CORES.texto, borderBottom: `1px solid #f1f5f9`, verticalAlign: 'middle' },
};

// Deriva um fundo claro a partir de uma cor de acento (para o "chip" do ícone do KPI).
// Mapa explícito para as cores semânticas mais usadas; fallback ao bg do acento.
function hexParaBg(hex) {
    const mapa = {
        '#e11d48': '#ffe4e6', '#3b82f6': '#eff6ff', '#10b981': '#ecfdf5',
        '#f59e0b': '#fffbeb', '#6366f1': '#e0e7ff', '#8b5cf6': '#f5f3ff',
        '#0ea5e9': '#e0f2fe', '#be123c': '#fff1f2',
        // Verdes dos cards Aprovados (#0baf13) e Taxa de Conversão (#047857) — sem estes,
        // caíam no fallback rosado (primariaBg) e ficava ícone verde sobre chip rosa.
        '#0baf13': '#ecfdf5', '#047857': '#ecfdf5',
    };
    return mapa[hex] || CORES.primariaBg;
}
